import {
  CompleteDepartmentPermissions,
  CompleteDispoPermission,
  CompleteDispoPermissions,
  CompletePoolerPermissions,
  CompleteRequesterPermission,
  DepartmentPermissions,
  DispoPermission,
  DispoPermissions,
  PoolerPermission,
  RequesterPermission,
} from '../../shared/models/dispoPermissions';

export function makePermissionsComplete(
  dispoPermissions: DispoPermissions,
): CompleteDispoPermissions {
  return {
    poolerAccess: makePoolerPermissionsComplete(dispoPermissions?.poolerAccess),
    dispoAccess: makeDispoPermissionsComplete(dispoPermissions?.dispoAccess),
    departments: makeDepartmentPermissionsComplete(
      dispoPermissions?.departments,
    ),
    requesterAccess: makeRequesterPermissionsComplete(
      dispoPermissions?.requesterAccess,
    ),
  };
}

export function makeDepartmentPermissionsComplete(
  departmentPermissions: DepartmentPermissions = {},
  debug = false,
): CompleteDepartmentPermissions {
  const defaultDepartmentPermissions = getDefaultDepartmentPermission();
  if (
    !departmentPermissions?.readAll &&
    Object.keys(departmentPermissions?.specificDepartmentIds ?? {})?.length
  ) {
    departmentPermissions.readAll = false;
  }
  if (
    !departmentPermissions?.writeAll &&
    Object.keys(departmentPermissions?.specificDepartmentIds ?? {})?.length
  ) {
    departmentPermissions.writeAll = false;
  }
  const completeDepartmentPermisison: CompleteDepartmentPermissions = {
    ...defaultDepartmentPermissions,
    ...(departmentPermissions || {}),
  };
  return completeDepartmentPermisison;
}

export function makePoolerPermissionsComplete(
  poolerPermissions?: PoolerPermission,
): CompletePoolerPermissions {
  const defaultPoolerPermissions = getDefaultPoolerPermission();
  return {
    ...defaultPoolerPermissions,
    ...(poolerPermissions || {}),
  };
}

export function makeRequesterPermissionsComplete(
  requesterPermissions?: RequesterPermission,
): CompleteRequesterPermission {
  const defaultPoolerPermissions = getDefaultRequesterPermission();
  return {
    ...defaultPoolerPermissions,
    ...(requesterPermissions || {}),
  };
}

export function makeDispoPermissionsComplete(
  dispoPermissions?: DispoPermission,
): CompleteDispoPermission {
  const defaultDispoPermissions = getDefaultDispoPermission();
  const managePermissions =
    dispoPermissions?.featurePermissions?.managePermissions;
  return {
    ...defaultDispoPermissions,
    ...(dispoPermissions || {}),
    featurePermissions: {
      managePermissions: {
        ...defaultDispoPermissions.featurePermissions.managePermissions,
        ...(managePermissions || {}),
      },
      manageTranscribeJobListPermissions: {
        ...defaultDispoPermissions.featurePermissions
          .manageTranscribeJobListPermissions,
        ...(dispoPermissions?.featurePermissions
          ?.manageTranscribeJobListPermissions || {}),
      },
      transferPermissions:
        dispoPermissions?.featurePermissions?.transferPermissions ||
        defaultDispoPermissions.featurePermissions.transferPermissions ||
        true,
      sharedPoolPermissions:
        dispoPermissions?.featurePermissions?.sharedPoolPermissions ||
        defaultDispoPermissions.featurePermissions.sharedPoolPermissions ||
        true,
      canApproveJobRequests:
        dispoPermissions?.featurePermissions?.canApproveJobRequests !==
        undefined
          ? dispoPermissions?.featurePermissions?.canApproveJobRequests
          : defaultDispoPermissions.featurePermissions.canApproveJobRequests,
      isCustomerAdmin:
        dispoPermissions?.featurePermissions?.isCustomerAdmin ||
        defaultDispoPermissions.featurePermissions.isCustomerAdmin ||
        false,
      hasJobAccess:
        dispoPermissions?.featurePermissions?.hasJobAccess !== undefined
          ? dispoPermissions?.featurePermissions?.hasJobAccess
          : defaultDispoPermissions.featurePermissions.hasJobAccess,
      hasEmploymentAccess:
        dispoPermissions?.featurePermissions?.hasEmploymentAccess !== undefined
          ? dispoPermissions?.featurePermissions?.hasEmploymentAccess
          : defaultDispoPermissions.featurePermissions.hasEmploymentAccess,
      hasRequestsAccess:
        dispoPermissions?.featurePermissions?.hasRequestsAccess !== undefined
          ? dispoPermissions?.featurePermissions?.hasRequestsAccess
          : defaultDispoPermissions.featurePermissions.hasRequestsAccess,
      canApproveHours:
        dispoPermissions?.featurePermissions?.canApproveHours !== undefined
          ? dispoPermissions?.featurePermissions?.canApproveHours
          : defaultDispoPermissions.featurePermissions.canApproveHours,
    },
  };
}

export function getDefaultPoolerPermission(): CompletePoolerPermissions {
  return {
    read: true,
    create: false,
    update: true,
    delete: false,
  };
}

export function getDefaultRequesterPermission(): CompleteRequesterPermission {
  return {
    read: true,
    create: false,
    update: true,
    delete: false,
  };
}

export function getDefaultDepartmentPermission(): CompleteDepartmentPermissions {
  return {
    readAll: true,
    writeAll: true,
    specificDepartmentIds: {},
  };
}

export function getDefaultDispoPermission(): CompleteDispoPermission {
  return {
    read: false,
    create: false,
    update: false,
    delete: false,
    featurePermissions: {
      managePermissions: {
        read: true,
        update: false,
      },
      manageTranscribeJobListPermissions: {
        read: true,
        update: true,
      },
      transferPermissions: true,
      sharedPoolPermissions: true,
      isCustomerAdmin: false,
      hasJobAccess: true,
      hasEmploymentAccess: true,
      hasRequestsAccess: false,
      canApproveJobRequests: false,
      canApproveHours: true,
    },
  };
}

export function getDefaultDispoPermissions(): CompleteDispoPermissions {
  return {
    poolerAccess: getDefaultPoolerPermission(),
    dispoAccess: getDefaultDispoPermission(),
    departments: getDefaultDepartmentPermission(),
    requesterAccess: getDefaultRequesterPermission(),
  };
}

export function getFullAccessDispoPermission(): CompleteDispoPermissions {
  return {
    poolerAccess: {
      read: true,
      create: true,
      update: true,
      delete: true,
    },
    departments: {
      readAll: true,
      writeAll: true,
      specificDepartmentIds: {},
    },
    dispoAccess: {
      read: true,
      create: true,
      update: true,
      delete: true,
      featurePermissions: {
        managePermissions: {
          read: true,
          update: true,
        },
        manageTranscribeJobListPermissions: {
          read: true,
          update: true,
        },
        hasRequestsAccess: true,
        hasEmploymentAccess: true,
        hasJobAccess: true,
        transferPermissions: true,
        sharedPoolPermissions: true,
        isCustomerAdmin: true,
        canApproveJobRequests: true,
        canApproveHours: true,
      },
    },
    requesterAccess: {
      read: true,
      create: true,
      update: true,
      delete: true,
    },
  };
}
